import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Doc } from "@stoneflow/backend/convex/_generated/dataModel"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { ArchiveIcon, ArchiveRestoreIcon, PencilIcon, PlusIcon, SparklesIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import { FieldLibraryEditor } from "#/components/field-library-editor"
import { RouteError } from "#/components/route-error"
import { Badge } from "#/components/ui/badge"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "#/components/ui/select"
import { Skeleton } from "#/components/ui/skeleton"
import { Switch } from "#/components/ui/switch"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "#/components/ui/table"

export const Route = createFileRoute("/_authenticated/admin/field-library")({
	beforeLoad: ({ context }) => {
		const roles = (context as { roles?: string[] }).roles ?? []
		if (!roles.includes("platform_admin")) {
			throw redirect({ to: "/admin" })
		}
	},
	errorComponent: RouteError,
	component: FieldLibraryAdmin,
})

const ALL_LOG_TYPES = "__all__"

function FieldLibraryAdmin() {
	const [filterSlug, setFilterSlug] = useState<string>(ALL_LOG_TYPES)
	const [includeArchived, setIncludeArchived] = useState(false)
	const [editorOpen, setEditorOpen] = useState(false)
	const [editing, setEditing] = useState<Doc<"fieldLibrary"> | null>(null)

	const logTypeDefs = useQuery(api.stonelog.logTypes.listDefinitions)
	const entries = useQuery(api.platform.fieldLibrary.list, {
		logTypeSlug: filterSlug === ALL_LOG_TYPES ? undefined : filterSlug,
		includeArchived,
	})

	const archive = useMutation(api.platform.fieldLibrary.archive)
	const unarchive = useMutation(api.platform.fieldLibrary.unarchive)
	const seedDefaults = useMutation(api.platform.seedFieldLibrary.seedDefaults)

	const logTypeOptions = useMemo(
		() => (logTypeDefs ?? []).map((d) => ({ slug: d.slug, name: d.name })),
		[logTypeDefs],
	)
	const slugToName = useMemo(() => {
		const m = new Map<string, string>()
		for (const lt of logTypeOptions) m.set(lt.slug, lt.name)
		return m
	}, [logTypeOptions])

	const selectedFilterName =
		filterSlug === ALL_LOG_TYPES ? "All log types" : slugToName.get(filterSlug) || filterSlug

	async function handleArchive(entry: Doc<"fieldLibrary">) {
		try {
			if (entry.isArchived) {
				await unarchive({ id: entry._id })
				toast.success(`"${entry.label}" restored`)
			} else {
				await archive({ id: entry._id })
				toast.success(`"${entry.label}" archived`)
			}
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to update entry")
		}
	}

	async function handleSeed() {
		try {
			const result = await seedDefaults({})
			toast.success(
				`Seeded ${result.created.length} entr${result.created.length === 1 ? "y" : "ies"} (${result.skipped.length} already existed)`,
			)
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to seed library")
		}
	}

	if (entries === undefined || logTypeDefs === undefined) {
		return <FieldLibrarySkeleton />
	}

	const isEmpty = entries.length === 0 && filterSlug === ALL_LOG_TYPES && !includeArchived

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader className="flex flex-row items-center justify-between gap-4">
					<div>
						<CardTitle>Field Library</CardTitle>
						<p className="mt-1 text-sm text-foreground-secondary">
							Reusable field definitions tenants pick when composing their schemas.
						</p>
					</div>
					<Button
						onClick={() => {
							setEditing(null)
							setEditorOpen(true)
						}}
					>
						<PlusIcon />
						Add field
					</Button>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex flex-wrap items-center gap-4">
						<div className="space-y-1">
							<label htmlFor="filter-log-type" className="text-technical mb-0.5 inline-block">
								Filter by log type
							</label>
							<Select value={filterSlug} onValueChange={setFilterSlug}>
								<SelectTrigger id="filter-log-type" className="min-w-48">
									<SelectValue>{selectedFilterName}</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ALL_LOG_TYPES}>All log types</SelectItem>
									{logTypeOptions.map((lt) => (
										<SelectItem key={lt.slug} value={lt.slug}>
											{lt.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex items-center gap-2 self-end pb-1">
							<Switch
								id="include-archived"
								checked={includeArchived}
								onCheckedChange={setIncludeArchived}
								size="sm"
							/>
							<label htmlFor="include-archived" className="text-sm">
								Include archived
							</label>
						</div>
					</div>

					{isEmpty ? (
						<div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-8 text-center">
							<SparklesIcon className="size-6 text-foreground-muted" />
							<p className="text-sm text-foreground-secondary">
								The library is empty. Seed it from the default schemas to get started.
							</p>
							<Button onClick={handleSeed}>
								<SparklesIcon />
								Seed defaults
							</Button>
						</div>
					) : entries.length === 0 ? (
						<p className="p-6 text-center text-sm text-foreground-secondary">
							No library entries match the current filter.
						</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Key</TableHead>
									<TableHead>Label</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Applicable log types</TableHead>
									<TableHead>State</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{entries.map((entry) => (
									<TableRow key={entry._id}>
										<TableCell className="font-mono text-xs">{entry.key}</TableCell>
										<TableCell className="font-medium">{entry.label}</TableCell>
										<TableCell>
											<Badge variant="secondary">{entry.type}</Badge>
										</TableCell>
										<TableCell>
											<div className="flex flex-wrap gap-1">
												{entry.applicableLogTypes.map((slug) => (
													<Badge key={slug} variant="outline">
														{slug === "*" ? "Any" : (slugToName.get(slug) ?? slug)}
													</Badge>
												))}
											</div>
										</TableCell>
										<TableCell>
											{entry.isArchived ? (
												<Badge variant="destructive">Archived</Badge>
											) : (
												<Badge variant="secondary">Active</Badge>
											)}
										</TableCell>
										<TableCell className="text-right">
											<div className="flex justify-end gap-1">
												<Button
													variant="ghost"
													size="icon-sm"
													onClick={() => {
														setEditing(entry)
														setEditorOpen(true)
													}}
													aria-label={`Edit ${entry.label}`}
												>
													<PencilIcon />
												</Button>
												<Button
													variant="ghost"
													size="icon-sm"
													onClick={() => handleArchive(entry)}
													aria-label={
														entry.isArchived ? `Unarchive ${entry.label}` : `Archive ${entry.label}`
													}
												>
													{entry.isArchived ? <ArchiveRestoreIcon /> : <ArchiveIcon />}
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<FieldLibraryEditor
				open={editorOpen}
				onOpenChange={setEditorOpen}
				logTypeOptions={logTypeOptions}
				entry={editing}
			/>
		</div>
	)
}

function FieldLibrarySkeleton() {
	return (
		<Card>
			<CardHeader>
				<Skeleton className="h-6 w-40" />
				<Skeleton className="mt-1 h-3 w-72" />
			</CardHeader>
			<CardContent className="space-y-3">
				{Array.from({ length: 5 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
					<Skeleton key={i} className="h-10 w-full" />
				))}
			</CardContent>
		</Card>
	)
}
